import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { utils, comnUtils, comnEnvs } from "@/comn/utils";
import { Page, Group, Layout, Button, FormControl } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage, usePopup } from "@/comn/hooks";
import {
    APIS,
    SF_BIDR_USR_INFO,
    SG_BIDR_USR_CTGR_LIST,
    URLS,
} from "@/tra/tancis/ptli/usr/bidrusr/services/PtliBidrUsrService";

export const PTLI0911004Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal();
    const { openPopup } = usePopup();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid });
    const toast = useToast();
    const { xtrnUsrUuid } = useParams();
    const [rtrnYn, setRtrnYn] = useState("N");

    // Track whether this user is deleted
    const [isDeleted, setIsDeleted] = useState(false); // <-- ADD THIS

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "COM0021" },
            { area: "comnCd", comnCd: "PTL0001" },
            { area: "cstmCd" },
            { area: "cntyCd" },
        ],
    });

    const form = {
        bidrUsrInfo: useForm({
            defaultSchema: SF_BIDR_USR_INFO,
        }),
    };

    const grid = {
        bidrUsrCtgrList: useGrid({ defaultSchema: SG_BIDR_USR_CTGR_LIST }),
    };

    const fetch = {
        getBidrUsrInfo: useFetch({
            api: () => APIS.getBidrUsrInfo(xtrnUsrUuid),
            enabled: !!xtrnUsrUuid,
            onSuccess: (data) => {
                const bidrUsrInfo = data.bidrUsrInfo.content;
                const bidrUsrCtgrList = bidrUsrInfo.bidrUsrCtgrList;

                form.bidrUsrInfo.setValues(bidrUsrInfo);
                grid.bidrUsrCtgrList.setData(comnUtils.getGridData(bidrUsrCtgrList));

                // Set deleted state based on delYn flag from API response
                setIsDeleted(bidrUsrInfo.delYn === "Y"); // <-- ADD THIS
            },
            onError: (error) => {},
            showToast: true,
        }),

        deleteBidrUsrInfo: useFetch({
            api: (xtrnUsrUuid) => APIS.deleteBidrUsrInfo(xtrnUsrUuid),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.00003",
                    onCancel: () => {
                        navigate(`${URLS.ptli0911001q}?menuId=${menuId}`);
                    },
                });
            },
            onError: (error) => {},
            showToast: true,
        }),

        updateBidrUsrInfo: useFetch({
            api: (info: any) => APIS.updateBidrUsrInfo(info),
            enabled: false,
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00059" });
                navigate(`${URLS.ptli0911001q}?menuId=${menuId}`);
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00022" }),
        }),

        updateBidrUsrPwdInit: useFetch({
            api: (data) => APIS.updateBidrUsrPwdInit(data),
            onSuccess: (data) => {
                modal.openModal({ content: "msg.ptl.00764" });
            },
            onError: (error) => {
                const data = error.response.data;
                modal.openModal({ content: data.errorMessage });
            },
        }),

        // Restore deleted bidder user  // <-- ADD THIS FETCH
        restoreBidrUsr: useFetch({
            api: (xtrnUsrUuid) => APIS.restoreBidrUsr(xtrnUsrUuid),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00059", // Successfully updated
                    onCancel: () => {
                        navigate(`${URLS.ptli0911001q}?menuId=${menuId}`);
                    },
                });
            },
            onError: (error) => {
                toast.showToast({ type: "error", content: "msg.com.00022" });
            },
            showToast: true,
        }),
    };

    const handler = {
        onDeleteBtnClick: () => {
            modal.openModal({
                content: "msg.00103",
                onConfirm: () => {
                    fetch.deleteBidrUsrInfo.fetch(xtrnUsrUuid);
                },
            });
        },

        onUpdateBtnClick: () => {
            if (!comnUtils.isEmpty(form.bidrUsrInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" });
                return;
            }

            const arrBidrUsrCtgr = grid.bidrUsrCtgrList.getData();
            const arrRow: String[] = [];

            let isCstmOfcr = false;
            arrBidrUsrCtgr.forEach((objPrmt) => {
                if (!comnUtils.isEmpty(objPrmt.actnCtgrCd)) {
                    arrRow.push(objPrmt.actnCtgrCd);
                    if (objPrmt.actnCtgrCd == 1) isCstmOfcr = true;
                }
            });

            const setRow = new Set(arrRow);
            if (setRow.size < arrRow.length) {
                toast.showToast({ type: "warning", content: "msg.00005" });
                return;
            }

            if (isCstmOfcr && comnUtils.isEmpty(form.bidrUsrInfo.getValue("cstmOfcrNo"))) {
                toast.showToast({ type: "warning", content: "msg.00002" });
                return;
            }

            form.bidrUsrInfo.handleSubmit(
                () => {
                    const data = form.bidrUsrInfo.getValues();
                    const bidrUsrCtgrList = grid.bidrUsrCtgrList.getData();
                    data.bidrUsrCtgrList = bidrUsrCtgrList;

                    modal.openModal({
                        content: "msg.com.00050",
                        onConfirm: () => {
                            fetch.updateBidrUsrInfo.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },

        onBtnGrdDelRowClick: () => {
            if (comnUtils.isEmpty(grid.bidrUsrCtgrList.getChecked())) {
                modal.openModal({ content: "msg.com.00086" });
                return;
            }
            modal.openModal({
                content: "msg.com.00049",
                onConfirm: () => {
                    grid.bidrUsrCtgrList.deleteRow("checkbox");
                },
            });
        },

        onResetBtnClick: () => {
            modal.openModal({
                content: "msg.ptl.00749",
                onConfirm: () => {
                    if (!comnUtils.isEmpty(form.bidrUsrInfo.errors)) {
                        toast.showToast({ type: "warning", content: "msg.00002" });
                        return;
                    }
                    form.bidrUsrInfo.handleSubmit(
                        () => {
                            const usrAplyInfo = form.bidrUsrInfo.getValues();
                            fetch.updateBidrUsrPwdInit.fetch(usrAplyInfo);
                        },
                        () => {
                            toast.showToast({ type: "warning", content: "msg.00002" });
                        },
                    )();
                },
            });
        },

        // Restore handler  // <-- ADD THIS HANDLER
        onRestoreBtnClick: () => {
            modal.openModal({
                content: "msg.com.00050", // Confirm update?
                onConfirm: () => {
                    fetch.restoreBidrUsr.fetch(xtrnUsrUuid);
                },
            });
        },
    };

    useEffect(() => {
        fetch.getBidrUsrInfo.fetch();
        form.bidrUsrInfo.setEditable(false);
        grid.bidrUsrCtgrList.setEdit("cell", "actnCtgrCd", false);
        grid.bidrUsrCtgrList.setEdit("cell", "useYn", false);
    }, []);

    return (
        <Page>
            <form>
                <Group>
                    <Group.Header>
                        <Group.Title title={t("T_BIDR_INFO")} />
                    </Group.Header>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.bidrUsrInfo.schema.xtrnUsrIdntfNo} />
                                <Group.Control {...form.bidrUsrInfo.schema.usrNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.bidrUsrInfo.schema.cstmOfcrNo}></Group.Control>
                                <Group.Control {...form.bidrUsrInfo.schema.usrStatCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.bidrUsrInfo.schema.frgnrYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.bidrUsrInfo.schema.aplcCntyCd}></Group.Control>
                                <Group.Control {...form.bidrUsrInfo.schema.usrMoblNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.bidrUsrInfo.schema.usrEmlAddr}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.bidrUsrInfo.schema.rsdtAddr}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.bidrUsrInfo.schema.mdfyRsn}></Group.Control>
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
                <Group>
                    <Group.Body>
                        <Group.Section gap={2}>
                            <Grid {...grid.bidrUsrCtgrList.grid} />
                        </Group.Section>
                    </Group.Body>
                </Group>
            </form>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0911001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    {/* Show Restore button only when user is deleted, Edit button otherwise */}
                    {isDeleted ? (
                            <Button color="success" onClick={handler.onRestoreBtnClick}>
                                {t("B_REVERT_USR")}
                            </Button>
                    ) : (
                        <Button
                            role="edit"
                            onClick={() => {
                                navigate(`${URLS.ptli0911003s}/${xtrnUsrUuid}`);
                            }}
                        ></Button>
                    )}
                </Layout.Right>
            </Layout>
        </Page>
    );
};